import { useQuery } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { branchTargetApi } from '@/services/branchTargetApi';

function ProgressBar({ label, target, achieved, color }: {
  label: string;
  target: number;
  achieved: number;
  color: string;
}) {
  const pct = target > 0 ? Math.min(Math.round((achieved / target) * 100), 100) : 0;
  const overTarget = target > 0 && achieved >= target;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700">{label}</span>
        <span className={`font-bold ${overTarget ? 'text-green-600' : 'text-gray-900'}`}>
          {achieved} / {target}
          <span className={`ml-1.5 ${overTarget ? 'text-green-600' : 'text-gray-400'}`}>
            ({pct}%)
          </span>
        </span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface TargetChasingChartProps {
  branchId?: string;
}

export default function TargetChasingChart({ branchId: propBranchId }: TargetChasingChartProps) {
  const user = useAuthStore((state) => state.user);
  const branchId = propBranchId || user?.activeBranch?.id || user?.branchId;

  const { data, isLoading } = useQuery({
    queryKey: ['targetSplit', branchId],
    queryFn: () => branchTargetApi.getTargetSplit(branchId!),
    enabled: !!branchId,
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (!data || !data.hasTarget) {
    return (
      <div className="border border-dashed border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Target className="h-4 w-4" />
          <span>No target set for this month</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-4 w-4 text-purple-600" />
        <h3 className="text-sm font-bold text-gray-900">Target Chasing</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Today */}
        <div className="space-y-2">
          <ProgressBar
            label="Today"
            target={data.today?.serviceCountTarget || 0}
            achieved={data.today?.serviceCountCompleted || 0}
            color="bg-blue-500"
          />
        </div>

        {/* This Week */}
        <div className="space-y-2">
          <ProgressBar
            label="This Week"
            target={data.thisWeek?.serviceCountTarget || 0}
            achieved={data.thisWeek?.serviceCountCompleted || 0}
            color="bg-purple-500"
          />
        </div>

        {/* This Month */}
        <div className="space-y-2">
          <ProgressBar
            label="This Month"
            target={data.thisMonth?.serviceCountTarget || 0}
            achieved={data.thisMonth?.serviceCountCompleted || 0}
            color="bg-green-500"
          />
        </div>
      </div>

      {/* Month pending note */}
      {data.thisMonth?.serviceCountPending > 0 && (
        <p className="text-[11px] text-gray-400 mt-3">
          {data.thisMonth.serviceCountPending} more services needed to hit monthly target
        </p>
      )}
    </div>
  );
}
